import { Component } from 'react';
import { connect } from 'react-redux';
import isNil from 'ramda/src/isNil';
import keys from 'ramda/src/keys';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import queryString from 'query-string';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import Rating from '@components/@core/Rating';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import { AverageTooltip } from '@components/ScorecardToolTips';
import AppActions from '~/actions/app';
import ManageActions from '~/actions/manage';
import { exportCsv } from '~/services/util-excel';
import { ReportMenu } from '~/services/config';
import withNavigate from '~/hocs/withNavigate';
import EmployeeList from './EmployeeList';
import OrgChart from './OrgChart';
import { CompanyPerformancePdf } from './CompanyPerformancePdf';

const moment = extendMoment(originalMoment);
const PERFORMANCE_TABS = [
    { id: 'employees', name: 'employees', label: 'Employees' },
    { id: 'chart', name: 'chart', label: 'OrgChart' },
];

class CompanyPerformance extends Component {
    state = {
        dateStart: this.props.location?.state?.dateStart || moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
        dateEnd: this.props.location?.state?.dateEnd || moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
        page: this.props.location?.state?.page || 1,
        perPage: this.props.location?.state?.per || 25,
        activeTab: queryString.parse(this.props.location?.search || '')?.active || 'employees',
    };

    componentDidMount() {
        const { companyIds } = this.props;
        const { page, perPage, dateStart, dateEnd } = this.state;
        this.handleFetch(companyIds, null, perPage, page, dateStart, dateEnd);
    }

    handleDateChange = (e) => {
        const { companyIds, userIds } = this.props;
        const { page, perPage } = this.state;
        const dateStart = moment(e.start).format('YYYY-MM-DD');
        const dateEnd = moment(e.end).format('YYYY-MM-DD');
        this.handleFetch(companyIds, userIds, perPage, page, dateStart, dateEnd);
        this.setState({ dateStart, dateEnd });
    };

    handleFetch = (companyIds, userIds, perPage, page, dateStart, dateEnd) => {
        let _companyIds, _userIds;
        if (companyIds[0] < 0) _companyIds = this.props.companies.map((item) => item.id);
        else _companyIds = companyIds;
        if (userIds === null || userIds.length === 0 || userIds[0] < 0) _userIds = null;
        else _userIds = userIds;
        const payload = {
            companyId: _companyIds,
            userId: _userIds,
            page,
            perPage,
            dateStart,
            dateEnd,
        };
        this.props.fetchCompanyReviews(payload);
    };

    handleFilter = (type, e) => {
        const { companyIds, userIds } = this.props;
        const { perPage, page, dateStart, dateEnd } = this.state;
        if (type === 'company') {
            const _companyIds = e.map((item) => item.id);
            this.handleFetch(_companyIds, userIds, perPage, page, dateStart, dateEnd);
        } else {
            const _userIds = e.map((item) => item.id);
            this.handleFetch(companyIds, _userIds, perPage, page, dateStart, dateEnd);
        }
    };

    handlePagination = (e) => {
        const { companyIds } = this.props;
        const { perPage, dateStart, dateEnd } = this.state;
        this.handleFetch(companyIds, null, perPage, e, dateStart, dateEnd);
        this.setState({ page: e });
    };

    handlePer = (perPage) => {
        const { companyIds } = this.props;
        const { dateStart, dateEnd } = this.state;
        let page = this.state.page;
        if (perPage >= 50) page = 1;
        this.handleFetch(companyIds, null, perPage, page, dateStart, dateEnd);
        this.setState({ page, perPage });
    };

    handleReport = (e) => {
        const userId = e?.performance ? e?.performance?.user_id : e?.user?.id;
        const companyId = e?.performance ? e?.performance?.company_id : e?.company?.id;
        const { dateStart, dateEnd } = this.state;
        this.props.fetchPersonalReviews(
            userId,
            { company_id: companyId, page: 1, per_page: 25, date_start: dateStart, date_end: dateEnd },
            `/hcm/report-performance/${userId}?date_start=${moment(dateStart).format('YYYY-MM-DD')}`
        );
    };

    handleSelectMenu = (data) => (e) => {
        const { dateStart, dateEnd } = this.state;
        const { scorecards, user } = data;

        switch (e) {
            case 'assign scorecard':
                this.props.toggleModal({
                    type: 'Assign ToDo',
                    data: {
                        before: {
                            modules: scorecards,
                            disabled: ['habits', 'habitslist', 'quotas'],
                            assignees: [user.id],
                            user,
                        },
                    },
                });
                break;

            case 'save actuals':
                this.props.toggleModal({
                    type: 'Save Actuals',
                    data: {
                        before: {
                            user,
                            date: moment(dateStart).format('YYYY-MM-DD'),
                        },
                    },
                });
                break;

            case 'start review':
                {
                    const userId = user.id;
                    const companyId = scorecards[0].company_id || Number(keys(user.data.company)[0]);
                    this.props.fetchIndividualReviews({ userId, companyId, dateStart, dateEnd }, `/hcm/performance-reviews/${userId}?date_start=${moment(dateStart).format('YYYY-MM-DD')}`);
                }
                break;

            case 'unassign scorecard': {
                const userId = user.id;
                const companyId = scorecards[0].company_id || Number(keys(user.data.company)[0]);
                const { page, perPage, dateStart, dateEnd } = this.state;
                const after = {
                    type: 'COMPANYPERFORMANCEREVIEWS_REQUEST',
                    payload: {
                        companyId,
                        userId,
                        page,
                        perPage,
                        dateStart,
                        dateEnd,
                        order: 'name',
                        sort: 'ASC',
                    },
                };
                this.props.toggleModal({
                    type: 'Confirm',
                    data: {
                        before: {
                            title: 'Unassign Scorecard',
                            body: 'This will permanently delete the scorecard from your assignments.  Are you sure?',
                        },
                    },
                    callBack: {
                        onYes: () => {
                            this.props.deleteScorecard({ scorecard: { id: scorecards[0].id } }, after);
                        },
                    },
                });
                break;
            }
            case 'view individual report':
                this.handleReport(data);
                break;
            case 'preview scorecard': {
                const date = moment.range(dateStart, dateEnd);
                const payload = {
                    type: 'Scorecard Detail',
                    data: { before: { scorecard: scorecards[0], date } },
                };
                this.props.toggleModal(payload);
                break;
            }
            default:
                break;
        }
    };

    handleExcelReport = () => {
        this.handlePer(this.props.data.total);
        this.props.toggleModal({
            type: 'Confirm',
            data: {
                before: {
                    title: 'Confirm',
                    body: 'Would you like to download this reports data to excel?',
                },
            },
            callBack: {
                onYes: () => {
                    this.handleExcel();
                },
            },
        });
    };

    handleExcel = () => {
        const { data } = this.props;
        const { activeTab } = this.state;
        const performance = [];
        activeTab === 'employees'
            ? data.users.forEach((item) => {
                  const excelData = {
                      Name: `${item.user.profile.first_name} ${item.user.profile.last_name}`,
                      Scorecard: item.scorecards.length ? item.scorecards[0].title : 'No Scorecard assigned',
                      Score: isNil(item.performance)
                          ? 'Incomplete'
                          : item.performance.status === 2
                            ? item.performance.data?.average_star_rating !== 'N/A'
                                ? item.performance.data.average_star_rating
                                : 'N/A'
                            : 'Incomplete',
                      Task: item.performance?.tasks.length || 'Incomplete',
                      Training: item.performance?.trainings.length || 'Incomplete',
                      Completed: item.performance?.status === 2 ? moment(item.performance.completed_at).format('MM/DD/YYYY') : 'Incomplete',
                  };
                  performance.push(excelData);
              })
            : data.departments.forEach((item) => {
                  const excelData = {
                      Department: item.department?.name || item.department,
                      Teams: 0,
                      Employees: item.totals?.employees?.count || 0,
                      Completed: item.totals?.reviews_completed?.count || 0,
                      Score: item.totals?.completed_average_star_score?.stars || 0,
                  };
                  performance.push(excelData);
              });
        const key = activeTab === 'employees' ? ['Name', 'Scorecard', 'Score', 'Task', 'Training', 'Completed'] : ['Department', 'Teams', 'Employees', 'Completed', 'Score'];

        exportCsv(performance, key, 'Reports-Performance', true);
        this.handlePer(25);
    };

    handleTabs = (e) => {
        this.props.navigate({ search: `?active=${e}` });
        this.setState({ activeTab: e });
    };

    handleReportMenu = (e) => {
        switch (e) {
            case 'export':
                this.handleExcelReport();
                break;
            case 'print': {
                const { data } = this.props;
                const { dateStart, dateEnd, activeTab } = this.state;
                const date = moment.range(dateStart, dateEnd);
                CompanyPerformancePdf({ date, data, activeTab });
                break;
            }
        }
    };

    render() {
        const { data } = this.props;
        const { dateStart, dateEnd, activeTab } = this.state;
        const date = moment.range(dateStart, dateEnd);

        return (
            <ErrorBoundary className="cmp-performance-reports">
                <Filter onChange={this.handleFilter} />
                <div className="card">
                    <div className="d-h-between ssp-t-2 pb-2 ssp-b-0">
                        <span className="dsl-b22 bold">Performance Reports</span>
                        <div className="mt-2 mt-md-0 d-flex">
                            <DatePicker
                                calendar="month"
                                append="caret"
                                format="MMM YY"
                                as="span"
                                align="right"
                                value={date}
                                disabledDate={(e) => moment(e).isAfter(moment().startOf('month'))}
                                onSelect={this.handleDateChange}
                            />
                            <EditDropdown className="ml-3" dataCy="editDropdown" options={ReportMenu} onChange={this.handleReportMenu} />
                        </div>
                    </div>
                    <div>
                        <ResponsiveTabs data={PERFORMANCE_TABS} active={activeTab} getId={(e) => e.name} getValue={(e) => e.label} onTab={(e) => this.handleTabs(e.id)} />
                        <Tab eventKey="employees" title="Employees" active={activeTab}>
                            <div className="mt-2">
                                <div className="ai-start avg-score d-inline-block">
                                    <span className="dsl-b14">Average Score</span>
                                    <div className="cursor-pointer pb-1 pt-1">
                                        <OverlayTrigger placement="right" overlay={AverageTooltip(data?.stats)}>
                                            <div>
                                                <Rating score={data?.stats?.completed_average_star_score?.stars} size="small" />
                                            </div>
                                        </OverlayTrigger>
                                    </div>
                                    <div>
                                        <span className="dsl-b12">
                                            {data?.stats?.reviews_completed?.count}/{data?.stats?.scorecards?.count}
                                            /Reviews completed
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <EmployeeList onReport={this.handleReport} onSelectMenu={this.handleSelectMenu} onPagination={this.handlePagination} onPer={this.handlePer} />
                        </Tab>
                        <Tab eventKey="chart" title="OrgChart" active={activeTab}>
                            <div className="mt-2">
                                <div className="ai-start avg-score d-inline-block">
                                    <span className="dsl-b14">Average Score</span>
                                    <div className="cursor-pointer pb-1 pt-1">
                                        <OverlayTrigger placement="right" overlay={AverageTooltip(data?.stats)}>
                                            <div>
                                                <Rating score={data?.stats?.completed_average_star_score?.stars} size="small" />
                                            </div>
                                        </OverlayTrigger>
                                    </div>
                                    <div>
                                        <span className="dsl-b12">
                                            {data?.stats?.reviews_completed?.count}/{data?.stats?.scorecards?.count}
                                            /Reviews completed
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <OrgChart onReport={this.handleReport} onSelectMenu={this.handleSelectMenu} />
                        </Tab>
                    </div>
                </div>
            </ErrorBoundary>
        );
    }
}

const mapStateToProps = (state) => ({
    companyIds: state.app.selectedCompany,
    userIds: state.app.selectedEmployee.users,
    companies: state.app.companies,
    data: state.manage.companyPerformanceReviews,
    users: state.app.users,
});

const mapDispatchToProps = (dispatch) => ({
    fetchIndividualReviews: (e, route) => dispatch(ManageActions.individualperformancereviewRequest(e, route)),
    fetchPersonalReviews: (id, e, route) => dispatch(ManageActions.getperformancereviewsRequest(id, e, route)),
    fetchCompanyReviews: (e) => dispatch(ManageActions.companyperformancereviewsRequest(e)),
    deleteScorecard: (e) => dispatch(ManageActions.postdeletescorecardRequest(e)),
    toggleModal: (e) => dispatch(AppActions.modalRequest(e)),
});

const CompanyPerformanceWithNavigate = withNavigate(CompanyPerformance);
export default connect(mapStateToProps, mapDispatchToProps)(CompanyPerformanceWithNavigate);
